import { useHasPermission } from '../../Utils/helpers.js';
import React, { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { useUIStore } from '@/store/uiStore';
import {
  Home,
  Users,
  Ticket,
  Calendar,
  Settings,
  BarChart,
  FileText,
  Shield,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Bell,
} from 'lucide-react';
import { AppLocalEnum, getLanguage, getText } from '@/translate/text';

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userRole } = useAuthStore();
  const { sidebarExpanded, toggleSidebar } = useUIStore();
  const language = getLanguage(localStorage.getItem('language')) || AppLocalEnum.EN;
  const isRTL = language === AppLocalEnum.AR;
  const navigation = [
    { name: getText('dashboard'), icon: Home, href: '/dashboard', permission: 'view_dashboard' },
    {
      name: getText('appointments'),
      icon: ClipboardList,
      href: '/appointments',
      permission: 'view_appointments',
    },
    { name: getText('patients'), icon: Users, href: '/patients', permission: 'view_patients' },
    { name: getText('calendar'), icon: Calendar, href: '/calendar', permission: 'view_calendar' },
    {
      name: getText('chatbot_tickets'),
      icon: Ticket,
      href: '/chatbot-tickets',
      permission: 'view_tickets',
    },
    {
      name: getText('analytics'),
      icon: BarChart,
      href: '/analytics',
      permission: 'view_analytics',
    },
    { name: getText('admin'), icon: Shield, href: '/admin', permission: 'access_admin_panel' },
    {
      name: getText('notification_center'),
      icon: Bell,
      href: '/notification-center',
      permission: 'view_notification_center',
    },
  ];

  // Filter navigation items based on permissions
  const filteredNavigation = navigation.filter((item) => {
    const { hasPermission, loading } = useHasPermission(item.permission);
    return !item.permission || hasPermission;
  });

  // Check if a nav item or its children are active
  const isNavItemActive = (item) => location.pathname === item.href;

  // Handles navigation for items with children
  const handleNavItemClick = (item) => {
    if (item.href) navigate(item.href);
  };

  return (
    <aside
      className={`
      fixed inset-y-0 ${isRTL ? 'right-0' : 'left-0'} z-30 flex flex-col
      transition-all duration-300 ease-in-out
      bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700
      ${sidebarExpanded ? 'w-64' : 'w-20'}
    `}
    >
      {/* Sidebar Header */}
      <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200 dark:border-gray-700">
        {sidebarExpanded && (
          <span className="text-lg font-semibold text-gray-900 dark:text-white">
            {getText('menu')}
          </span>
        )}
        <button
          onClick={toggleSidebar}
          className="p-2 rounded-lg text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          {sidebarExpanded ? (
            <ChevronLeft className="h-5 w-5" />
          ) : (
            <ChevronRight className="h-5 w-5" />
          )}
        </button>
      </div>

      {/* Navigation Menu */}
      <nav className="flex-1 overflow-y-auto px-3 py-4">
        <ul className="space-y-1">
          {filteredNavigation.map((item) => (
            <li key={item.name}>
              <NavLink
                to={item.href}
                className={({ isActive }) => `
                  group flex items-center gap-x-3 rounded-lg p-2 text-sm font-medium
                  ${
                    isActive
                      ? 'bg-primary-50 text-primary-600 dark:bg-primary-900 dark:text-primary-300'
                      : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700'
                  }
                `}
              >
                <item.icon
                  className={`
                    h-5 w-5
                    ${isNavItemActive(item) ? 'text-primary-600 dark:text-primary-300' : 'text-gray-500 dark:text-gray-400'}
                  `}
                />
                {sidebarExpanded && <span className="flex-1">{item.name}</span>}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </aside>
  );
};

// Optimization: Memoize the component to prevent unnecessary re-renders
export default React.memo(Sidebar, (prevProps, nextProps) => {
  // Custom comparison logic if needed
  return true;
});
